import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const ResponsiveTable = ({ segments }) => {
  const [searchParams] = useSearchParams();
  const region = searchParams.get("region");
  const country = searchParams.get("country_name");
  const title = country ? country : region;
  const [segmentData, setSegmentData] = useState([]);
  const [countriesCount, setCountriesCount] = useState(0);

  useEffect(() => {
    if (!segments || !segments.segments) return;

    let filteredData = [];

    if (country) {
      setCountriesCount(1);
      // Filter data for a single country
      filteredData = segments.segments.filter((s) => s.country === country);
    } else if (region) {
      // Filter all countries under the region
      filteredData = segments.segments.filter((s) => s.region === region);
    }

    // If dealing with a region, aggregate data from all countries
    if (region && filteredData.length > 0) {
      let regionSegmentsMap = {};
      let totalCAGR = 0;
      let countryCount = 0;

      filteredData.forEach((ele) => {
        ele.segments.forEach((segment) => {
          if (!regionSegmentsMap[segment.name]) {
            regionSegmentsMap[segment.name] = {
              name: segment.name,
              subSegments: [],
            };
          }

          segment.subSegments.forEach((subSegment) => {
            let existingSubSegment = regionSegmentsMap[
              segment.name
            ].subSegments.find((s) => s.name === subSegment.name);

            if (!existingSubSegment) {
              regionSegmentsMap[segment.name].subSegments.push({
                name: subSegment.name,
                years: subSegment.years.map((y) => ({
                  year: y.year,
                  value: y.value,
                })),
                cagr: subSegment.cagr || 0, // Handle undefined CAGR
              });
            } else {
              // Sum values across all countries for each year
              subSegment.years.forEach((y, index) => {
                existingSubSegment.years[index].value += y.value;
              });

              // Sum CAGR values
              existingSubSegment.cagr += subSegment.cagr || 0;
            }
          });
        });

        // Sum CAGR across all countries in the region
        totalCAGR += ele.segments.reduce(
          (sum, segment) =>
            sum +
            (segment.subSegments.reduce((s, sub) => s + (sub.cagr || 0), 0) ||
              0),
          0
        );

        countryCount++;
      });

      // Compute the average CAGR for the region
      // let averageCAGR = countryCount > 0 ? totalCAGR / countryCount : 0;
      let averageCAGR = totalCAGR / countryCount;
      setCountriesCount(countryCount);
      // Convert regionSegmentsMap to an array
      const aggregatedSegments = Object.values(regionSegmentsMap);

      setSegmentData([{ region, segments: aggregatedSegments, averageCAGR }]);
    } else {
      // If only country is selected, set data normally
      setSegmentData(
        filteredData.map((ele) => ({
          country: ele.country,
          region: ele.region,
          segments: ele.segments,
        }))
      );
    }
  }, [country, region, segments]);
  const handleDownloads = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    segmentData.forEach((segmentItem) => {
      segmentItem.segments.forEach((segment) => {
        csvContent += `\n${segment.name}\n`;
        let headers = ["Sub-Segment"];
        let years =
          segment.subSegments.length > 0
            ? segment.subSegments[0].years.map((y) => y.year)
            : [];
        headers = headers.concat(years, ["CAGR"]);
        csvContent += headers.join(",") + "\n";

        segment.subSegments.forEach((subSegment) => {
          let row = [subSegment.name];
          row = row.concat(
            subSegment.years.map((y) => y.value.toFixed(2) || "-")
          );
          row.push(
            subSegment.cagr
              ? `${
                  region
                    ? (subSegment.cagr / countriesCount).toFixed(2)
                    : (Math.floor(subSegment.cagr * 100) / 100).toFixed(2)
                }%`
              : "-"
          );
          csvContent += row.join(",") + "\n";
        });
      });
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "comprehensive_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <>
      <button
        onClick={handleDownloads}
        className="bg-black text-white py-3 px-4 rounded-lg mb-2 font-inter text-sm hover:bg-[#21242a]"
      >
        Download comprehensive report
      </button>
      {segmentData.length > 0 &&
        segmentData[0].segments?.map((segment, i) => (
          <div
            key={i}
            className="bg-white overflow-hidden my-8 rounded-xl shadow"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto font-inter">
                <thead>
                  <tr className="bg-lightPink border-b text-sm">
                    <th className="px-4 py-4 text-left font-semibold">
                      {segment?.name}
                    </th>
                    {segment?.subSegments?.[0]?.years?.map(
                      (yearItem, index) => (
                        <th
                          key={index}
                          className="px-4 py-4 text-left font-semibold"
                        >
                          {yearItem?.year}
                        </th>
                      )
                    )}
                    <th className="px-4 py-4 text-left font-semibold">
                      CAGR ({segment?.subSegments?.[0]?.years?.[0]?.year} -{" "}
                      {segment?.subSegments?.[0]?.years?.slice(-1)[0]?.year})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {segment?.subSegments?.map((subSegment, subIndex) => (
                    <tr key={subIndex} className="border-b text-sm">
                      <td className="px-4 py-4 font-semibold">
                        {subSegment?.name}
                      </td>
                      {subSegment?.years?.map((yearData, yearIndex) => (
                        <td key={yearIndex} className="px-4 py-4">
                          {yearData?.value?.toFixed(2) || "-"}
                        </td>
                      ))}
                      <td className="px-4 py-4">
                        {subSegment?.cagr
                          ? `${
                              region
                                ? (subSegment?.cagr / countriesCount).toFixed(2)
                                : (
                                    Math.floor(subSegment?.cagr * 100) / 100
                                  ).toFixed(2)
                            }%`
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </>
  );
};

export default ResponsiveTable;
